import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getUploads, getSnapshots, cancelUpload } from "@/lib/api";
import { fmtNum, fmtMoney } from "@/lib/format";
import { UploadCloud, CheckCircle2, AlertTriangle, FileSpreadsheet, Lock, X, Ban } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function DataUpload() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  const [jobId, setJobId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const { data: uploads } = useQuery({ queryKey: ["uploads"], queryFn: getUploads });
  const { data: snapshots } = useQuery({ queryKey: ["snapshots"], queryFn: getSnapshots });

  const reset = () => { setProgress(0); setStage(""); setJobId(null); setCancelling(false); };

  const handle = async (file) => {
    if (!isAdmin) return;
    if (!file) return;
    setBusy(true); setResult(null); setError(null); setProgress(0); setStage("Uploading file…"); setCancelling(false);
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { timeout: 120000 });
      const jid = res.data.job_id;
      setJobId(jid);
      const started = Date.now();
      // poll job status (processing happens in the background to avoid proxy timeouts)
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = await api.get(`/upload/status/${jid}`, { timeout: 60000 });
        const d = st.data;
        if (typeof d.progress === "number") setProgress(d.progress);
        if (d.stage) setStage(d.stage);
        if (d.status === "done") {
          setResult(d.result); setProgress(100);
          toast.success("Inventory data refreshed");
          qc.invalidateQueries();
          break;
        }
        if (d.status === "cancelled") {
          toast.info("Upload cancelled — existing inventory kept unchanged");
          break;
        }
        if (d.status === "failed") {
          setError(d.errors && d.errors.length ? d.errors : ["Upload failed"]);
          toast.error("Upload failed");
          break;
        }
        if (Date.now() - started > 15 * 60 * 1000) {
          setError(["Processing is taking longer than expected. Please check back shortly."]);
          break;
        }
      }
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors || [e?.response?.data?.detail || "Upload failed"];
      setError(Array.isArray(errs) ? errs : [String(errs)]);
      toast.error("Upload failed");
    } finally { setBusy(false); reset(); if (inputRef.current) inputRef.current.value = ""; }
  };

  const handleCancel = async () => {
    if (!jobId || cancelling) return;
    setCancelling(true); setStage("Cancelling…");
    try { await cancelUpload(jobId); } catch { /* status poll will reflect outcome */ }
  };

  const prev = snapshots && snapshots.length >= 2 ? snapshots[snapshots.length - 2] : null;
  const cur = snapshots && snapshots.length ? snapshots[snapshots.length - 1] : null;

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div>
        <div className="label-eyebrow">Data</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Data Upload</h1>
        <p className="text-sm text-slate-500 mt-0.5">Upload the latest daily inventory Excel. Lead-time overrides, settings and history are preserved.</p>
      </div>

      {!isAdmin ? (
        <div className="kcard p-10 text-center border-2 border-dashed border-[var(--line)]">
          <Lock className="w-9 h-9 mx-auto text-slate-300" />
          <div className="text-sm font-medium text-slate-600 mt-3">Read-only account</div>
          <div className="text-[12px] text-slate-400 mt-1">Uploading a new daily file requires an administrator account.</div>
        </div>
      ) : busy ? (
        <div className="kcard p-6" data-testid="upload-progress-card">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate" data-testid="upload-stage-label">
                  {cancelling ? "Cancelling…" : (stage || "Processing…")}
                </div>
                <div className="text-[12px] text-slate-400">Large files can take ~30 seconds to process.</div>
              </div>
            </div>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="upload-cancel-button"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-[13px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {cancelling ? <Ban className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} Cancel
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${Math.max(progress, 4)}%`, background: "var(--kcsa-red)" }}
                data-testid="upload-progress-bar"
              />
            </div>
            <div className="text-[13px] font-semibold text-slate-700 num w-10 text-right" data-testid="upload-progress-pct">{progress}%</div>
          </div>
        </div>
      ) : (
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={`kcard kcard-click p-10 text-center border-2 border-dashed ${drag ? "border-[var(--kcsa-red)] bg-red-50/40" : "border-[var(--line)]"}`}>
        <UploadCloud className="w-10 h-10 mx-auto text-slate-400" />
        <div className="text-sm font-medium text-slate-700 mt-3">Drag & drop your Excel file here, or click to browse</div>
        <div className="text-[12px] text-slate-400 mt-1">.xlsx daily inventory extract · handles 150,000+ rows</div>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="upload-input" onChange={(e) => handle(e.target.files[0])} />
      </div>
      )}

      {error && (
        <div className="kcard p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Validation Errors</div>
          <ul className="mt-2 text-[13px] text-red-600 list-disc pl-5">{error.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {result && (
        <div className="kcard p-5 border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-3"><CheckCircle2 className="w-4 h-4" /> Upload Successful</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[["File Name", result.filename], ["Upload Date", new Date(result.uploaded_at).toLocaleString()], ["Source Rows", fmtNum(result.source_rows)], ["Unique Item Codes", fmtNum(result.unique_items)], ["Warehouses", fmtNum(result.warehouses)], ["Total SOH Value", fmtMoney(result.total_soh_value)]].map(([l, v]) => (
              <div key={l}><div className="label-eyebrow">{l}</div><div className="text-[15px] font-semibold text-slate-800 num mt-0.5">{v}</div></div>
            ))}
          </div>
        </div>
      )}

      {cur && (
        <div className="kcard p-5">
          <div className="label-eyebrow mb-3">Latest KPI Snapshot {prev ? "· change vs previous upload" : ""}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Trend label="Total SOH Value" cur={cur.total_soh_value} prev={prev?.total_soh_value} money />
            <Trend label="Unique Items" cur={cur.unique_items} prev={prev?.unique_items} />
            <Trend label="PO Required" cur={cur.po_required_count} prev={prev?.po_required_count} />
            <Trend label="Recommended PO Value" cur={cur.recommended_po_value} prev={prev?.recommended_po_value} money />
            <Trend label="Stock Outs" cur={cur.stockout_count} prev={prev?.stockout_count} />
            <Trend label="Critical" cur={cur.critical_count} prev={prev?.critical_count} />
            <Trend label="Overstock Excess" cur={cur.overstock_excess_value} prev={prev?.overstock_excess_value} money />
            <Trend label="Non-Movers" cur={cur.nonmover_count} prev={prev?.nonmover_count} />
          </div>
        </div>
      )}

      <div className="kcard p-0 overflow-hidden">
        <div className="label-eyebrow px-4 pt-4 pb-2">Upload History</div>
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--surface)]"><tr>{["File", "Date", "Source Rows", "Unique Items", "Warehouses", "SOH Value"].map((h) => <th key={h} className="thead-cell px-4 py-2 text-left">{h}</th>)}</tr></thead>
          <tbody>
            {(uploads || []).map((u, i) => (
              <tr key={i} className="border-t border-[var(--line)]">
                <td className="px-4 py-2 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600" />{u.filename}</td>
                <td className="px-4 py-2 text-slate-600">{new Date(u.uploaded_at).toLocaleString()}</td>
                <td className="px-4 py-2 num">{fmtNum(u.source_rows)}</td>
                <td className="px-4 py-2 num">{fmtNum(u.unique_items)}</td>
                <td className="px-4 py-2 num">{fmtNum(u.warehouses)}</td>
                <td className="px-4 py-2 num">{fmtMoney(u.total_soh_value)}</td>
              </tr>
            ))}
            {(!uploads || uploads.length === 0) && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Trend({ label, cur, prev, money }) {
  const fmt = money ? fmtMoney : fmtNum;
  const delta = prev != null ? cur - prev : null;
  const pct = prev ? (delta / Math.abs(prev)) * 100 : null;
  const up = delta > 0;
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="text-[17px] font-bold text-slate-800 num mt-0.5">{fmt(cur)}</div>
      {delta != null && delta !== 0 && <div className={`text-[11px] num ${up ? "text-red-600" : "text-emerald-600"}`}>{up ? "▲" : "▼"} {fmt(Math.abs(delta))}{pct != null ? ` (${Math.abs(pct).toFixed(1)}%)` : ""}</div>}
    </div>
  );
}
